import { useMemo } from 'react'

import { type Event } from '@tribeplatform/gql-client/types'
import { useEventAnalytics } from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../../i18n/index.js'
import { StatCard, ValueType } from '../components/StatCard.js'

interface Props {
  event: Event
}

export const EventAnalytics = ({ event }: Props) => {
  const { $t } = useI18n()
  const { data, isLoading } = useEventAnalytics({
    variables: {
      eventRecurrenceIds: [event.recurrenceId],
    },
  })
  const { uniqueVisitors, registrantsCount, conversionRatio } = useMemo(() => {
    const record = data?.records?.at(0)
    const uniqueVisitors =
      record?.payload?.find(p => p.key === 'unique_visitor_count')?.value ?? 0
    const registrantsCount =
      record?.payload?.find(p => p.key === 'registrant_count')?.value ?? 0
    const conversionRatio =
      record?.payload?.find(p => p.key === 'conversion_ratio')?.value ?? 0

    return { uniqueVisitors, registrantsCount, conversionRatio }
  }, [data])

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-s-4">
        <StatCard
          title={$t({
            id: 'Analytics.Event.TotalUniqueVisitors',
            defaultMessage: 'Total unique visitors',
          })}
          value={uniqueVisitors}
          isLoading={isLoading}
          className="flex-1"
        />
        <StatCard
          title={$t({
            id: 'Analytics.Event.TotalRegistrants',
            defaultMessage: 'Total registrants',
          })}
          value={registrantsCount}
          isLoading={isLoading}
          className="flex-1"
        />
        <StatCard
          title={$t({
            id: 'Analytics.Event.ConversionRate',
            defaultMessage: 'Conversion rate',
          })}
          value={conversionRatio}
          isLoading={isLoading}
          valueType={ValueType.Percentage}
          tooltip={$t({
            id: 'Analytics.Event.ConversionRateTooltip',
            defaultMessage:
              'Conversion rate is the percentage of unique visitors who have registered for the event.',
          })}
          className="flex-1"
        />
      </div>
    </div>
  )
}
